import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import copy from "clipboard-copy";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { publicShowSale, publicCheckPaymentStatus } from "@/Api/sales";
import { PIX_STATUS } from "@/utils/constants";

interface Transaction {
  id: number;
  uuid: string;
  id_transaction: string;
  emv?: string | null;
  pix_copy_and_paste?: string;
  pix_key: string;
  amount: string;
  url_qr_code: string;
  status: string;
}

interface Sale {
  id: number;
  uuid: string;
  total: string;
  client: {
    name: string;
  };
  transactions: Transaction[];
  payment_forms: Array<{
    payment_form: {
      id: number;
      name: string;
    };
  }>;
}

export function PublicPaymentPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();
  const pixIndexParam = searchParams.get("pix");
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSale = useCallback(async () => {
    if (!uuid) {
      setError("UUID da venda não fornecido");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, ok } = await publicShowSale(uuid);

      if (!ok) {
        setError("Venda não encontrada ou não disponível");
        return;
      }

      setSale(data?.response);
    } catch (err) {
      setError("Erro ao carregar dados da venda");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  const checkPaymentStatus = useCallback(async () => {
    if (!sale || checking) return;

    try {
      setChecking(true);
      const { data, ok } = await publicCheckPaymentStatus(String(sale.id));

      if (!ok) return;

      const updatedTransactions = data?.response || [];

      const updatedSale = {
        ...sale,
        transactions: updatedTransactions,
      };
      setSale(updatedSale);

      const pixTransaction = updatedTransactions.find(
        (t: Transaction) => t.pix_copy_and_paste && t.status === PIX_STATUS.PAID
      );

      if (pixTransaction) {
        toast.success("Pagamento confirmado!");
      }
    } catch (err) {
      toast.error("Erro ao verificar status do pagamento");
    } finally {
      setChecking(false);
    }
  }, [sale, checking]);

  const handleCopyPix = async (pixCode: string, index: number) => {
    try {
      await copy(pixCode);
      setCopiedIndex(index);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopiedIndex(null), 3000);
    } catch (error) {
      toast.error("Erro ao copiar código PIX");
    }
  };

  useEffect(() => {
    fetchSale();
  }, [fetchSale]);

  useEffect(() => {
    if (!sale) return;

    const hasUnpaidPixTransaction = sale.transactions.some(
      (t: Transaction) => t.pix_copy_and_paste && t.status !== PIX_STATUS.PAID
    );

    if (!hasUnpaidPixTransaction) return;

    const interval = setInterval(checkPaymentStatus, 10000);
    return () => clearInterval(interval);
  }, [sale, checkPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Carregando dados do pagamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Erro</h2>
              <p className="text-gray-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-12 h-12 text-gray-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Venda não encontrada
              </h2>
              <p className="text-gray-600">
                A venda solicitada não foi encontrada.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filtra apenas transações PIX
  const pixTransactions = sale.transactions.filter(
    (t: Transaction) => t.pix_copy_and_paste
  );

  // Se tiver parâmetro pix, filtra apenas aquela transação
  const selectedPixIndex = pixIndexParam !== null ? parseInt(pixIndexParam, 10) : null;
  const transactionsToShow = selectedPixIndex !== null && pixTransactions[selectedPixIndex]
    ? [pixTransactions[selectedPixIndex]]
    : pixTransactions;

  const allPaid = pixTransactions.every((t) => t.status === PIX_STATUS.PAID);

  const renderPixTransaction = (transaction: Transaction, index: number, displayIndex: number) => {
    const isPaid = transaction.status === PIX_STATUS.PAID;

    return (
      <div key={transaction.id} className="flex flex-col items-center p-6 bg-white dark:bg-zinc-800 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">
          {pixTransactions.length > 1 && selectedPixIndex === null
            ? `PIX #${displayIndex + 1}`
            : "Pagamento PIX"}
        </h3>

        {isPaid ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="bg-green-100 p-4 rounded-full mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
            <h4 className="text-xl font-bold text-green-600 mb-2">
              Pagamento Confirmado!
            </h4>
            <p className="text-center text-gray-600 mb-4">
              O pagamento de{" "}
              {Mask.formatCurrency(parseFloat(transaction.amount))} foi
              confirmado com sucesso.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white p-4 rounded-lg mb-4">
              <QRCode
                value={transaction.pix_copy_and_paste || ""}
                size={192}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>

            <div className="w-full max-w-xs mb-4">
              <p className="text-sm text-center mb-2">Valor a pagar:</p>
              <p className="text-2xl font-bold text-center text-green-600 mb-4">
                {Mask.formatCurrency(parseFloat(transaction.amount))}
              </p>

              <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-zinc-700 rounded-md">
                <p className="text-xs flex-1 truncate">
                  {transaction.pix_copy_and_paste}
                </p>
                <Button
                  size="icon"
                  onClick={() =>
                    handleCopyPix(transaction.pix_copy_and_paste || "", index)
                  }
                  className="h-8 w-8 shrink-0"
                >
                  {copiedIndex === index ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="w-full max-w-xs space-y-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={checkPaymentStatus}
                  disabled={checking}
                >
                  {checking ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    "Verificar pagamento"
                  )}
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                O status do pagamento é verificado automaticamente a cada 10
                segundos.
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Pagamento PIX</CardTitle>
            <p className="text-sm text-gray-600">Venda #{sale.id}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">Cliente:</p>
                <p className="font-medium">{sale.client.name}</p>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">Valor total:</p>
                <p className="text-2xl font-bold text-green-600">
                  {Mask.formatCurrency(parseFloat(sale.total))}
                </p>
              </div>

              {pixTransactions.length > 1 && selectedPixIndex === null && (
                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500">
                    Esta venda possui {pixTransactions.length} pagamentos PIX
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {pixTransactions.length > 0 ? (
          <div className="space-y-4">
            {transactionsToShow.map((transaction, index) => {
              const originalIndex = pixTransactions.findIndex(t => t.id === transaction.id);
              return renderPixTransaction(transaction, originalIndex, index);
            })}

            {allPaid && pixTransactions.length > 1 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-lg font-semibold text-green-700 mb-2">
                      Todos os pagamentos confirmados!
                    </h3>
                    <p className="text-green-600">
                      Todos os {pixTransactions.length} pagamentos PIX foram confirmados.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <AlertCircle className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  PIX não disponível
                </h3>
                <p className="text-gray-600">
                  Esta venda não possui transação PIX disponível para
                  visualização.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
